import { type ReactNode, useRef, useState } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/admin/coaching/coaching-dashboard/shared/tooltip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { MAX_NOTE_LENGTH, toStoredNote } from '@/lib/coaching/note-limits'
import type {
  ProblemAttemptDto,
  ProblemHistoryDto,
} from '@/lib/coaching/problem-history-query'
import type { TrayCard } from '../_lib/auto-place'
import { parseCardReview } from '../_lib/card-review'
import { parseProblemAttempts, parseProblemHistory } from '../_lib/history-log'
import {
  canRedateCard,
  type MoveTargetWeek,
  redateBlockedReason,
} from '../_lib/move-targets'
import type {
  CardDetailsResult,
  CardDetailsSaveResult,
  CardReviewState,
  CurriculumSlice,
} from '../_state/contracts'
import { CardReviewSection } from './card-review-section'
import { ProblemHistorySection } from './problem-history-section'
import { RelatedProblemChips } from './related-problem-chips'
import { HEADING_CLASS } from './section-heading'
import { SourceMark } from './source-mark'
import { SourceSwitcher } from './source-switcher'

// Filler for the two renders where `ProblemHistorySection` never looks at
// its `history`/`attempts` props: the loading state (it shows only "Loading…")
// and — because that state can be reached while `cardDetails` still holds the
// *previous* card's data, mid-refetch — the moment just before it. Passing
// the stale data through there would risk a future edit to the section
// reading it; passing this instead makes that impossible to get wrong.
const NO_HISTORY_YET: ProblemHistoryDto = {
  problemId: '',
  platformAttemptCount: 0,
  coachReported: false,
  coachReportedAt: null,
  lastSeenAt: null,
}
const NO_ATTEMPTS_YET: readonly ProblemAttemptDto[] = []

export interface CardDetailsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * The card being shown, or null when the coach has the dialog open on a card
   * the board no longer holds — it was cancelled elsewhere, or the page of the
   * plan it sat on was dropped by a refetch. That is a state the coach has to
   * be told about, not one the dialog may silently disappear into, so `open`
   * is the caller's intent and this is only the content.
   */
  card: TrayCard | null
  /**
   * Rendered under the title. Null means the card is in the staging tray and
   * has no date yet. The container formats it, because the modal has no calendar
   * of its own — `moveTargets` is where its day arithmetic comes from.
   */
  assignedDateLabel: string | null
  /**
   * Every day on the board with its legality for this card (LG-360 §4), built
   * by `buildMoveTargets`. Empty or omitted renders no picker at all — which is
   * what a board with no weeks yet should show.
   */
  moveTargets?: readonly MoveTargetWeek[]
  /**
   * Assigns the card to a day. The container decides whether that is a `place`
   * (the card is in the tray) or a `move` (it is already on a day) — the modal
   * knows only that the coach picked a date.
   */
  onAssignToDay?: (weekIdx: number, dayIdx: number) => void
  // Taken from the slice that implements them rather than restated, so adding
  // a compare-and-swap baseline to a write changes one signature, not two.
  onSaveCoachNote?: CurriculumSlice['saveCoachNote']
  onSaveUniversalNotes?: CurriculumSlice['saveUniversalNotes']
  onSetCompletion?: CurriculumSlice['setAssignmentCompletion']
  /**
   * Writes the coach's source pick (LG-360 §7). Undefined leaves the title
   * row showing the card's current source as plain text — the same
   * "nothing to write with" gate `onSaveCoachNote` et al. use — and, when
   * the fetch has landed with more than one candidate source, renders the
   * switcher itself.
   */
  onSaveSource?: CurriculumSlice['saveSource']
  /**
   * The open card's history, sources and relatives (LG-360 §5/§7/§8), or null
   * before the fetch the container drives (via `setDetailsProblemId`) has
   * landed — including while the card has no catalog problem behind it, since
   * there is then nothing to fetch. `cardDetails.history`/`.attempts` carry
   * `Date`s serialized to ISO strings for the tRPC trip (`CardDetailsResult` =
   * `CoachingTransportDto<CardDetailsDto>`); this modal is the boundary that
   * converts them back via `parseProblemHistory`/`parseProblemAttempts` before
   * anything downstream — namely `summarizeHistory`/`describeAttempt` in
   * `_lib/history-log.ts` — sees a `Date`.
   */
  cardDetails?: CardDetailsResult | null
  /** True while the fetch above is in flight. */
  cardDetailsPending?: boolean
  /**
   * True when the most recent fetch for the open problem failed. Distinct
   * from the merely-null `cardDetails` a not-yet-started fetch also has —
   * without this the modal cannot tell "errored" apart from "hasn't been
   * asked for yet", and would either surface a false failure on the latter
   * or (as before this flag existed) stay silent on the former, which reads
   * indistinguishably from a card with no problem behind it at all.
   */
  cardDetailsError?: boolean
  /**
   * Where the open card's review read has got to (LG-360 §3), as one tagged
   * state naming the card it is about — see `CardReviewState`, which is
   * also where the reason it is not a payload-plus-flags triple lives.
   * Null while the modal is closed; omitted entirely by a slice that does
   * no review read at all (the mock board), which renders no review section
   * rather than one stuck on "Loading…".
   */
  cardReview?: CardReviewState | null
  /**
   * Opens the secondary related-problem modal (§8) on the chip the coach
   * clicked. Omitted, or a card with no relatives yet, renders no chips.
   *
   * This `Dialog` never varies its own `modal`-ness for the related modal's
   * sake (fix-round 1 removed an earlier `modal` prop that did): Radix's
   * `DialogContent` renders one of two entirely different component types
   * depending on `Dialog`'s `modal` prop, and toggling it on every
   * related-chip click was remounting this whole dialog — silently
   * discarding any unsaved `NoteEditor` draft. `RelatedProblemModal` fixes
   * the two-overlay problem on its own side instead, by never painting a
   * second backdrop over this one's — see its module doc.
   */
  onOpenRelated?: (problemId: string) => void
}

const TYPE_LABEL: Record<TrayCard['type'], string> = {
  coding: 'Coding',
  sd: 'System design',
  behavioral: 'Behavioral',
  interview: 'Interview',
  other: 'Other',
}

function SectionHeading(props: { children: ReactNode }): ReactNode {
  return <h3 className={HEADING_CLASS}>{props.children}</h3>
}

/**
 * One note editor: a textarea over a fixed baseline, an explicit Save, and its
 * own error line.
 *
 * Explicit save rather than auto-save because `setUniversalNotes` is
 * compare-and-swap — it needs the exact text the edit was based on, and a
 * debounced writer would race its own in-flight baseline.
 *
 * The baseline is state seeded from the prop, not the prop itself, and it moves
 * only on a save this editor performed. Tracking the prop would defeat the CAS:
 * a refetch carrying another coach's edit would slide under the coach's typing
 * and turn the conflict the check exists to catch into a silent overwrite.
 * Callers pass a `key` so opening a different card mounts a fresh editor rather
 * than inheriting the previous card's draft.
 */
function NoteEditor(props: {
  id: string
  label: string
  description: string
  placeholder: string
  initialBaseline: string | null
  disabled?: boolean
  disabledReason?: string
  onSave: (input: {
    draft: string | null
    expected: string | null
  }) => Promise<CardDetailsSaveResult>
}): ReactNode {
  const {
    id,
    label,
    description,
    placeholder,
    initialBaseline,
    disabled = false,
    disabledReason,
    onSave,
  } = props
  const [baseline, setBaseline] = useState(initialBaseline)
  const [draft, setDraft] = useState(initialBaseline ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // Measured on the value that actually goes over the wire, not the raw draft:
  // the server validates what `toStoredNote` produced, so counting the untrimmed
  // text would refuse a full-length note that picked up a trailing newline.
  const stored = toStoredNote(draft)
  const dirty = stored !== baseline
  const overBy = (stored?.length ?? 0) - MAX_NOTE_LENGTH
  const tooLong = overBy > 0

  const handleSave = async (): Promise<void> => {
    if (!dirty || tooLong || saving) return
    setSaving(true)
    setError(null)
    setSaved(false)
    const result = await onSave({ draft: stored, expected: baseline })
    setSaving(false)
    if (result.error === null) {
      // The text we just wrote is what the next compare-and-swap must expect.
      setBaseline(stored)
      setSaved(true)
    } else setError(result.error)
  }

  const hint = tooLong
    ? `Too long by ${overBy} characters`
    : dirty
      ? 'Unsaved changes'
      : ''

  return (
    <section className="flex flex-col gap-1.5">
      {/* Both a heading and a real label: the heading keeps the section
          navigable, and `htmlFor` is what names the field — an `aria-label`
          here would win over the visible text and leave the two free to drift. */}
      <h3 className={HEADING_CLASS}>
        <label htmlFor={id}>{label}</label>
      </h3>
      <p
        className="text-[10px] leading-normal text-(--cd-text-faint)"
        id={`${id}-description`}
      >
        {/* When the field is disabled the reason is the only thing worth
            saying, and it goes here rather than in the placeholder: a disabled
            textarea is out of the tab order and its placeholder is not part of
            its accessible name, so a reason left there reaches nobody. */}
        {disabled && disabledReason !== undefined
          ? disabledReason
          : description}
      </p>
      <textarea
        aria-describedby={`${id}-description ${id}-hint`}
        aria-invalid={tooLong}
        className="min-h-[72px] w-full resize-y rounded-lg border border-(--cd-border-2) bg-(--cd-bg) px-2.5 py-2 text-[11.5px] leading-relaxed text-(--cd-text) outline-none placeholder:text-(--cd-text-ghost) focus:border-(--cd-border-4) disabled:cursor-not-allowed disabled:opacity-50"
        disabled={disabled}
        id={id}
        onChange={(event) => {
          setDraft(event.target.value)
          setSaved(false)
          // A failure describes the save it came from. Once the text has moved
          // on it describes nothing the coach can still see, and "someone else
          // edited these notes first" sitting over a rewritten draft reads as a
          // fresh conflict that never happened.
          setError(null)
        }}
        placeholder={placeholder}
        value={draft}
      />
      <div className="flex items-center gap-2">
        <button
          aria-label={`Save ${label.toLowerCase()}`}
          className="cursor-pointer rounded-md border-0 bg-(--cd-green) px-2.5 py-1 text-[10px] font-bold text-(--cd-accent-contrast) hover:bg-(--cd-green-hover) disabled:cursor-not-allowed disabled:opacity-45"
          disabled={disabled || saving || !dirty || tooLong}
          onClick={() => void handleSave()}
          type="button"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {/* The live region carries the outcome of a save and nothing else. The
            dirty hint and the over-limit count both change on every keystroke,
            and announcing those politely means a screen reader narrating the
            coach's own typing, one character at a time, across a 4000-character
            field. They live in the description instead, where they are read on
            arrival at the field and on demand. */}
        <span
          aria-live="polite"
          className="text-[10px] text-(--cd-text-faint)"
          role="status"
        >
          {error ?? (saved ? 'Saved' : '')}
        </span>
        <span
          className={`text-[10px] ${tooLong ? 'text-(--cd-red)' : 'text-(--cd-text-faint)'}`}
          id={`${id}-hint`}
        >
          {hint}
        </span>
      </div>
    </section>
  )
}

/**
 * Completion control (LG-360 §6, part one).
 *
 * Only `manual` assignments are offered a toggle: the service hard-blocks
 * force-completing anything the platform tracks (`planning-service.ts`
 * `completeManual`), so rendering a toggle there would be an affordance whose
 * every use returns an error. Coach override of tracked tasks is §6's second
 * half and needs its own audit trail, so the tracked case says what governs it
 * instead of pretending to offer a switch.
 *
 * A card with no completion mode at all — the mock board builds cards without
 * one — is a third case, not a synonym for the tracked one. Saying "completes
 * when you review the submission" about a card whose mode is simply unknown is
 * a claim the component is in no position to make.
 */
function CompletionControl(props: {
  card: TrayCard
  onSetCompletion?: CurriculumSlice['setAssignmentCompletion']
}): ReactNode {
  const { card, onSetCompletion } = props
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const completed = card.status === 'completed' || card.done === true
  const mode = card.completionMode
  const cancelled = card.status === 'cancelled'

  const handleToggle = async (): Promise<void> => {
    if (!onSetCompletion || pending) return
    setPending(true)
    setError(null)
    const result = await onSetCompletion({
      cardId: card.id,
      target: completed ? 'active' : 'completed',
    })
    setPending(false)
    setError(result.error)
  }

  const heading =
    mode === 'manual'
      ? 'Marked done by the coach'
      : mode === 'submission_review'
        ? 'Completes when you review the submission'
        : 'Completion'
  const detail =
    mode === 'manual'
      ? 'Nothing on the platform tracks this one.'
      : mode === 'submission_review'
        ? 'Overriding a platform-tracked task is not built yet.'
        : 'How this card completes is not recorded on this board.'

  return (
    <section className="flex flex-col gap-1.5 rounded-lg border border-(--cd-border-2) bg-(--cd-inset) px-3 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col">
          <span className="text-[11.5px] font-semibold text-(--cd-text)">
            {heading}
          </span>
          <span className="text-[10px] text-(--cd-text-faint)">{detail}</span>
        </div>
        {cancelled ? (
          <span className="shrink-0 whitespace-nowrap text-[10px] font-bold text-(--cd-red)">
            Cancelled
          </span>
        ) : mode === 'manual' && onSetCompletion ? (
          <button
            aria-label={
              completed
                ? `Mark ${card.title} as not completed`
                : `Mark ${card.title} as completed`
            }
            className="shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-(--cd-border-2) bg-(--cd-bg) px-2.5 py-1 text-[10px] font-bold text-(--cd-text) hover:border-(--cd-border-4) disabled:cursor-not-allowed disabled:opacity-45"
            disabled={pending}
            onClick={() => void handleToggle()}
            type="button"
          >
            {pending ? 'Saving…' : completed ? 'Mark not done' : 'Mark done'}
          </button>
        ) : (
          <span className="shrink-0 whitespace-nowrap text-[10px] font-bold text-(--cd-text-muted)">
            {completed ? 'Done' : 'Not done yet'}
          </span>
        )}
      </div>
      <span
        aria-live="polite"
        className="text-[10px] text-(--cd-red)"
        role="status"
      >
        {error ?? ''}
      </span>
    </section>
  )
}

/**
 * The assigned-day picker (LG-360 §4).
 *
 * A grouped `<select>` rather than a month calendar, because the set of legal
 * targets is not "any date" — it is exactly the `normal` days of the draft weeks
 * the board is holding. A calendar would render mostly-unpickable dates and
 * still have nowhere to say why each one is unpickable, where a disabled
 * `<option>` carries its reason in the text a screen reader already reads. It
 * also comes with date keyboard handling, typeahead and mobile pickers for free.
 *
 * Illegal days are rendered disabled with their reason rather than dropped: a
 * coach looking for Thursday needs to learn that its week is published, not that
 * Thursday does not exist.
 *
 * The move is an explicit button, not the select's `onChange`. Radix focuses
 * the first tabbable element in the dialog on open, and — with the robot held
 * out of the tab order — this select is usually it; a coach who opened the
 * card and pressed an arrow key would re-date it, since on Windows Chrome an
 * arrow on a focused select changes the value outright rather than opening
 * the menu. (When §7's source switcher is showing — a card with more than one
 * candidate source — its trigger sits earlier in the header and takes that
 * opening focus instead; the switcher's own trigger is a plain button, so an
 * arrow key there does nothing.) Confirming also matches the note editors
 * above, which are explicit-save for their own reasons, and stays true either
 * way regardless of which control actually opened focused.
 */
function AssignedDayPicker(props: {
  card: TrayCard
  moveTargets: readonly MoveTargetWeek[]
  onAssignToDay: (weekIdx: number, dayIdx: number) => void
}): ReactNode {
  const { card, moveTargets, onAssignToDay } = props
  const id = `assigned-day-${card.id}`

  const current = moveTargets
    .flatMap((week) => week.days)
    .find((day) => day.current)
  // Empty means "showing the card's own day" — the coach has picked nothing yet.
  const [choice, setChoice] = useState('')

  if (!canRedateCard(card)) {
    // `move` and `place` both require a `planned`/`in_progress` assignment, so a
    // completed, cancelled or submitted card has no legal target at all. Saying
    // so beats a select whose every option the server would refuse.
    // `redateBlockedReason` is a checked `Record<AssignmentStatus, …>` (B4):
    // it used to be a two-armed ternary that told the coach to "mark it not
    // done first" about a `submitted` card, which is neither completed nor
    // markable — `CompletionControl` offers no such toggle for one. The
    // fallback below covers only the `fixed`/`done` cases, which block
    // re-dating without a status of their own to explain why.
    return (
      <p className="text-[10px] text-(--cd-text-faint)">
        {redateBlockedReason(card) ?? 'This card cannot be rescheduled.'}
      </p>
    )
  }

  const picked = choice !== '' && choice !== current?.value

  return (
    <div className="flex items-center gap-2">
      <label
        className="text-[10px] font-bold tracking-wide text-(--cd-text-faint) uppercase"
        htmlFor={id}
      >
        Move to
      </label>
      <select
        className="min-w-0 flex-1 rounded-md border border-(--cd-border-2) bg-(--cd-bg) px-1.5 py-1 text-[10.5px] text-(--cd-text) focus:border-(--cd-border-4) focus:outline-none"
        id={id}
        onChange={(event) => setChoice(event.currentTarget.value)}
        value={choice || (current?.value ?? '')}
      >
        {/* A tray card has no day yet, so the select needs something truthful to
            display before the coach picks one. Left enabled because a disabled
            option cannot be the rendered value in every browser. */}
        {current === undefined ? (
          <option value="">Not scheduled — pick a day</option>
        ) : null}
        {moveTargets.map((week) => (
          <optgroup key={week.weekId} label={week.label}>
            {week.days.map((day) => (
              <option
                disabled={day.disabledReason !== null}
                key={day.value}
                value={day.value}
              >
                {day.label}
                {day.disabledReason !== null ? ` — ${day.disabledReason}` : ''}
                {/* Budget is advisory: the service lets a coach overfill a day
                    on purpose, so this warns without preventing. */}
                {day.disabledReason === null && day.overBudget
                  ? ' — over budget'
                  : ''}
                {day.current ? ' · current' : ''}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <button
        aria-label={`Move ${card.title} to the selected day`}
        className="shrink-0 cursor-pointer rounded-md border-0 bg-(--cd-green) px-2 py-1 text-[10px] font-bold text-(--cd-accent-contrast) hover:bg-(--cd-green-hover) disabled:cursor-not-allowed disabled:opacity-45"
        disabled={!picked}
        onClick={() => {
          const [weekIdx, dayIdx] = choice.split(':').map(Number)
          if (weekIdx === undefined || Number.isNaN(weekIdx)) return
          if (dayIdx === undefined || Number.isNaN(dayIdx)) return
          onAssignToDay(weekIdx, dayIdx)
          // The board is about to re-render with the card on its new day, which
          // becomes the select's `current`. Dropping the choice hands the value
          // back to it instead of pinning the select to a now-stale pick.
          setChoice('')
        }}
        type="button"
      >
        Move
      </button>
    </div>
  )
}

const CONTENT_CLASS =
  'coaching-dashboard max-h-[85vh] gap-0 overflow-y-auto border-(--cd-border-4) bg-(--cd-panel) px-[18px] py-4 text-(--cd-text) shadow-[0_24px_60px_rgba(0,0,0,.6)] sm:max-w-[620px]'

/**
 * The admin card-details modal (LG-360 §1).
 *
 * Admin-native rather than a reuse of the client's `TaskDetailDialog`: that one
 * reads `HomeworkContext`, which is client-scoped and not exported, and it
 * renders the client's *view* of a card — no note authoring, no coach-only
 * sections. This one is fed straight from `AdminAssignmentDto` by way of
 * `TrayCard`, and mirrors the client dialog's anatomy so the two stay legible
 * side by side.
 *
 * §3's review read (the read-only review section below — status and
 * comments for the card's latest submission; authoring stays in the Review
 * tab), §4 (the assigned-date picker), §5 (problem history, below), §7 (the
 * external id and source switcher, on the title row) and §8 (the
 * related-problem chips, also on the title row, the secondary modal a chip
 * opens — `RelatedProblemModal`, a sibling this modal knows nothing about —
 * and that modal's three writes: stage to the tray, assign alongside the
 * current card, and swap it in) have all shipped. Only §6's coach override
 * of a platform-tracked completion remains.
 *
 * Two things this plan deliberately did not build, both flagged for a human
 * decision rather than silently skipped: a writer for `ProblemVariant` (§8's
 * chips read duplicate/variant/series relationships that already exist in
 * the catalog; nothing here lets a coach create or edit one), and §7's
 * disabled rendering for a source a card cannot actually be assigned from
 * (there is no column recording which sources are assignable, so every
 * source in the switcher renders the same regardless).
 */
export function CardDetailsModal(props: CardDetailsModalProps): ReactNode {
  const {
    open,
    onOpenChange,
    card,
    assignedDateLabel,
    moveTargets,
    onAssignToDay,
    onSaveCoachNote,
    onSaveUniversalNotes,
    onSetCompletion,
    onSaveSource,
    cardDetails,
    cardDetailsPending = false,
    cardDetailsError = false,
    cardReview,
    onOpenRelated,
  } = props

  /**
   * What was on screen the last time the dialog was open.
   *
   * `DialogContent` animates out over 200ms and Radix keeps it mounted for the
   * whole of it, while the caller drops the card and clears `open` in the same
   * commit. Reading `card` during that window would flash "no longer on the
   * board" over every ordinary close, so the closing dialog animates out
   * showing what the coach was actually looking at. (Not unit-tested: jsdom
   * runs no animations, so Radix unmounts synchronously there and the window
   * this exists for never opens.)
   */
  const lastOpen = useRef<{
    card: TrayCard | null
    assignedDateLabel: string | null
    moveTargets: readonly MoveTargetWeek[]
    cardDetails: CardDetailsResult | null
    cardDetailsPending: boolean
    cardDetailsError: boolean
    cardReview: CardReviewState | null | undefined
  } | null>(null)
  // `moveTargets` rides along for the same reason: the container derives them
  // from the card it just dropped, so reading the live prop during the exit
  // animation would empty the picker out from under the closing dialog.
  // `cardDetails`/`cardDetailsPending`/`cardDetailsError` ride along too — the
  // container clears `detailsProblemId` in the same commit that clears
  // `card`, and reading the live props during the exit animation would
  // collapse the history section to "loading" (or drop it) under a dialog
  // that is still fading out. `cardReview` rides along for exactly the same
  // reason — the container clears `detailsAssignmentId` on that same commit.
  const snapshot = {
    card,
    assignedDateLabel,
    moveTargets: moveTargets ?? [],
    cardDetails: cardDetails ?? null,
    cardDetailsPending,
    cardDetailsError,
    // Not `?? null`, unlike its neighbours: `undefined` here means the slice
    // driving this modal does no review read at all (the mock board), which
    // has to stay distinguishable from a real read whose answer has not
    // landed — collapsing them would leave the mock board showing a review
    // section stuck on "Loading…" forever.
    cardReview,
  }
  if (open) lastOpen.current = snapshot
  const showing = open ? snapshot : lastOpen.current

  // §7. Whether the source switcher is currently expanded, reported by the
  // switcher itself via `onExpandedChange` — the dialog's own Escape
  // handling is what actually has to know this (see `onEscapeKeyDown`
  // below), because a coach with the switcher open pressing Escape must
  // collapse it first, not close the whole dialog underneath it.
  const [sourceSwitcherExpanded, setSourceSwitcherExpanded] = useState(false)

  // Nothing ever reset `sourceSwitcherExpanded` on the close path: it is
  // reachable only from the switcher's own `onExpandedChange`, which never
  // fires on unmount. `CardDetailsModal` is mounted once, permanently, by
  // its container (no `key`), so a coach who expanded the switcher and then
  // closed the dialog with the ✕ or an outside click — both go through
  // `onOpenChange`, never through `setSourceSwitcherExpanded` — would find
  // `sourceSwitcherExpanded` still `true` for the rest of the session, and
  // `onEscapeKeyDown` below would swallow every future Escape for every
  // card, including ones with no switcher at all. Every close, from either
  // Dialog below, goes through this wrapper so the stuck-`true` state is
  // impossible.
  const handleOpenChange = (next: boolean): void => {
    if (!next) setSourceSwitcherExpanded(false)
    onOpenChange(next)
  }

  // Never opened, and not open now — there is nothing to animate out.
  if (showing === null) return null

  // Open on a card that is no longer there. Unmounting instead would drop the
  // dialog out from under whatever the coach was typing, and — because closing
  // this way never fires `onOpenChange` — leave the caller still holding the
  // id, so re-loading that page of the plan would pop the dialog open again
  // with no one having asked for it.
  if (showing.card === null) {
    return (
      <Dialog onOpenChange={handleOpenChange} open={open}>
        <DialogContent className={CONTENT_CLASS}>
          <DialogHeader className="gap-1.5 pr-8 text-left">
            <DialogTitle className="text-[14px] leading-snug font-bold">
              This card is no longer on the board
            </DialogTitle>
            <DialogDescription className="text-[10.5px] text-(--cd-text-faint)">
              It was removed, or the part of the plan it sat on is no longer
              loaded. Close this and open the card again from the board.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    )
  }

  // Everything below reads the card being shown, which during a close is the
  // one the coach had open rather than whatever the board holds now.
  const shown = showing.card
  const shownDateLabel = showing.assignedDateLabel
  const details = shown.compatibilityDescription ?? shown.instructions ?? null
  const learningItemId = shown.learningItemId ?? null

  // §5. A card with no catalog problem behind it (a one-off card) has no
  // history to have, and renders nothing — not "no history", which is a
  // claim about a problem the client has never seen, and not an error
  // message, which is a claim about a fetch that never happened. While
  // loading, `showing.cardDetails` may still be null (the fetch has not
  // landed yet) or holding the *previous* card's data (a refetch in
  // flight); either way the section itself decides what "pending" renders,
  // so it is shown whenever there is a problem to ask about and either an
  // answer, a fetch in flight, or a known failure for it. The remaining
  // case — a problem, no data, not pending, not errored — is the one tick
  // before the first fetch for a newly-opened problem starts, and stays
  // silent like the no-problem case, but for an unrelated reason: there is
  // nothing true to say yet, not nothing to say ever.
  const hasProblem = shown.problemId != null
  const showHistory =
    hasProblem &&
    (showing.cardDetailsPending ||
      showing.cardDetails !== null ||
      showing.cardDetailsError)

  // §3. The review read answers about an assignment, so — unlike everything
  // above, which is keyed on the problem — a state counts only when it names
  // the card being shown. A state for another card is the previous card's,
  // still in hand for the render before this card's own fetch starts, so it
  // reads as pending here whatever phase it claims.
  const reviewState =
    showing.cardReview && showing.cardReview.assignmentId === shown.id
      ? showing.cardReview
      : null
  const loadedReview =
    reviewState?.phase === 'loaded' ? reviewState.review : null
  const failedReview = reviewState?.phase === 'error'
  // Shown for a card the platform tracks (where "nothing submitted yet" is
  // itself the answer a coach came for), for any card that turns out to
  // have a review regardless of how it completes, and whenever the read
  // failed — a failure is not a "no review" answer, and a manual card may
  // have a review the fetch never delivered. Only a manual card that
  // *answered* "no review" says nothing here — `CompletionControl` above
  // has already said nothing on the platform tracks it, and repeating that
  // as an empty review section would be noise. Gated on the slice doing
  // this read at all, so the mock board renders no section rather than a
  // permanent "Loading…".
  const showReview =
    showing.cardReview !== undefined &&
    (shown.completionMode === 'submission_review' ||
      loadedReview !== null ||
      failedReview)

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent
        className={CONTENT_CLASS}
        // Mirrors `profile-editor-dialog.tsx`'s `saving` gate on the same
        // prop: a plain boolean decides whether this Escape is this
        // dialog's to have. Reachable and authoritative rather than a race
        // — this runs as part of Radix's own capture-phase Escape handling
        // (`DismissableLayer`'s `useEscapeKeydown`), so `preventDefault`
        // here is checked by Radix itself before it ever calls `onDismiss`.
        // It also composes with a second, later-stacked dialog (LG-360
        // §9/Task 10): a lower `DismissableLayer`'s Escape handling no-ops
        // while a higher one is mounted, so only the switcher's own dialog
        // is ever a candidate to swallow its Escape.
        onEscapeKeyDown={(event) => {
          if (sourceSwitcherExpanded) event.preventDefault()
        }}
      >
        <DialogHeader className="gap-1.5 pr-8 text-left">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-(--cd-border)/40 px-2 py-0.5 text-[9.5px] font-bold text-(--cd-text-muted)">
              {TYPE_LABEL[shown.type]}
            </span>
            {/* §7. This source's own id for the problem, e.g. LeetCode's "1" —
                omitted rather than shown as a placeholder when the card has
                none, which a one-off card and a problem with no id on its
                current source both are. */}
            {shown.externalId != null ? (
              <span className="text-[9.5px] font-semibold text-(--cd-text-muted)">
                #{shown.externalId}
              </span>
            ) : null}
            {/* §7. The switcher needs `sourceOptions`, which only exists once
                the details fetch has landed — until then (and for a card with
                no problem, or no writer supplied) this falls back to the
                same static mark the switcher itself renders for a
                single-source problem. The `problemId` check is defence in
                depth against the same mid-refetch window `ProblemHistorySection`
                already guards against above: `showing.cardDetails` can still
                hold the *previous* card's fetch for one render while `shown`
                has already moved on, and without this check the switcher
                would offer card B's source options under card A's title. */}
            {onSaveSource &&
            showing.cardDetails &&
            showing.cardDetails.problemId === shown.problemId &&
            showing.cardDetails.sourceOptions.length > 0 ? (
              <SourceSwitcher
                card={shown}
                onExpandedChange={setSourceSwitcherExpanded}
                onPick={(sourceId) =>
                  onSaveSource({ cardId: shown.id, sourceId })
                }
                options={showing.cardDetails.sourceOptions}
              />
            ) : shown.sourceDisplayName ? (
              <span className="flex items-center gap-1 text-[9.5px] font-semibold text-(--cd-text-muted)">
                <SourceMark icon={shown.sourceIcon} decorative />
                {shown.sourceDisplayName}
              </span>
            ) : null}
            {/* §8. The chips and the robot share the header's dead space at
                upper right, as one `ml-auto` group — moved here from the
                robot's own button so the chips fill the space directly
                beside it rather than packing left against the source mark.
                Chips first, robot last: the robot is deliberately the very
                last, untabbable thing in the header (see below), and a chip
                landing after it would sit behind an inert control instead of
                before it. */}
            <div className="ml-auto flex items-center gap-1.5">
              {/* Same defence in depth as the source switcher above: without
                  the `problemId` check, a mid-refetch render could show card
                  B's title with card A's related-problem chips underneath
                  it, and "Swap in instead of" would replace B with A's
                  relative. */}
              {onOpenRelated &&
              showing.cardDetails &&
              showing.cardDetails.problemId === shown.problemId ? (
                <RelatedProblemChips
                  onOpen={onOpenRelated}
                  related={showing.cardDetails.related}
                />
              ) : null}
              {/* §9: the affordance is reserved, not wired. It must never be
                  bound to the schedule card's `convertAiMock` — that flips the
                  card to a hidden-title AI mock, a different feature entirely.
                  Out of the tab order because Radix focuses the first tabbable
                  thing in the dialog on open, and this is it: every coach opening
                  a card would land on a button that does nothing, and a screen
                  reader would announce "coming soon" before the card's own name.
                  `aria-disabled` alone would not have kept it out — Radix filters
                  on the `disabled` property, which this deliberately lacks. */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    aria-disabled="true"
                    aria-label="Convert to AI interview — coming soon"
                    className="cursor-not-allowed border-0 bg-transparent p-0 text-[11px] opacity-40"
                    onClick={(event) => event.preventDefault()}
                    tabIndex={-1}
                    type="button"
                  >
                    🤖
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  Convert to an AI interview — coming soon
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
          <DialogTitle className="text-[14px] leading-snug font-bold text-balance">
            {shown.title}
          </DialogTitle>
          <DialogDescription className="text-[10.5px] text-(--cd-text-faint)">
            {/* From `showing`, not the prop: the caller clears the label on
                close along with the card, so reading the prop here would tell a
                scheduled card it was in the staging tray for the length of the
                exit animation. */}
            {shownDateLabel === null
              ? 'In the staging tray — not scheduled yet'
              : `Assigned for ${shownDateLabel}`}
          </DialogDescription>
          {/* §4. Only offered when the container supplied both the days and a
              writer: the mock board has no plan days to move between. */}
          {onAssignToDay && showing.moveTargets.length > 0 ? (
            <AssignedDayPicker
              card={shown}
              // Keyed like the note editors: opening a different card must not
              // inherit the day the coach had picked but not confirmed on the
              // last one.
              key={`assigned-day-${shown.id}`}
              moveTargets={showing.moveTargets}
              onAssignToDay={onAssignToDay}
            />
          ) : null}
        </DialogHeader>

        <div className="mt-3 flex flex-col gap-4">
          <CompletionControl card={shown} onSetCompletion={onSetCompletion} />

          {details ? (
            <section className="flex flex-col gap-1.5">
              <SectionHeading>Details</SectionHeading>
              <p className="text-[11.5px] leading-relaxed whitespace-pre-line text-(--cd-text-muted)">
                {details}
              </p>
            </section>
          ) : null}

          {showHistory ? (
            <ProblemHistorySection
              attempts={
                showing.cardDetails
                  ? parseProblemAttempts(showing.cardDetails.attempts)
                  : NO_ATTEMPTS_YET
              }
              history={
                showing.cardDetails
                  ? parseProblemHistory(showing.cardDetails.history)
                  : NO_HISTORY_YET
              }
              pending={showing.cardDetailsPending}
              error={
                !showing.cardDetailsPending &&
                showing.cardDetails === null &&
                showing.cardDetailsError
              }
            />
          ) : null}

          {showReview ? (
            <CardReviewSection
              error={reviewState?.phase === 'error'}
              // Anything that is not this card's own settled answer: no
              // state for it yet, or one still in flight.
              pending={reviewState === null || reviewState.phase === 'pending'}
              // The boundary where the wire's ISO strings become `Date`s, the
              // same job `parseProblemHistory` does one section up.
              review={loadedReview ? parseCardReview(loadedReview) : null}
            />
          ) : null}

          {onSaveCoachNote ? (
            <NoteEditor
              description="Written for this client on this card. They see it as soon as the week is published — before they start, not only after review."
              id={`coach-note-${shown.id}`}
              initialBaseline={shown.coachNote ?? null}
              key={`coach-note-${shown.id}`}
              label="Coaching note"
              onSave={({ draft }) =>
                onSaveCoachNote({ cardId: shown.id, note: draft })
              }
              placeholder="Guidance for this assignment…"
            />
          ) : null}

          {onSaveUniversalNotes ? (
            <NoteEditor
              description="About the problem itself, not this assignment — every client ever assigned it sees these, now and in future."
              disabled={learningItemId === null}
              disabledReason="This card has no catalog item behind it, so it has nothing universal to say."
              id={`universal-notes-${shown.id}`}
              initialBaseline={shown.universalNotes ?? null}
              key={`universal-notes-${shown.id}`}
              label="About this problem"
              onSave={({ draft, expected }) =>
                learningItemId === null
                  ? Promise.resolve({
                      error: 'This card has no catalog item behind it.',
                    })
                  : onSaveUniversalNotes({
                      learningItemId,
                      notes: draft,
                      expectedNotes: expected,
                    })
              }
              placeholder="e.g. a real-world version of this algorithm…"
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  )
}
